import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { Service } from 'app/models/services';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService, UploadResponse, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

type ImageUploadMode = 'direct' | 'imageproduit';

@Component({
  selector: 'app-ajouter-services',
  templateUrl: './ajouter-services.component.html',
  styleUrls: ['./ajouter-services.component.scss']
})
export class AjouterServicesComponent implements OnInit {
  service: Service = new Service();
  agenciesList: any[] = [];

  agencyControl = new FormControl('');
  filteredAgencies: Observable<any[]>;

  selectedFiles: FileList | null = null;
  uploadedMainImage: string | null = null;
  videoUrl: string = '';

  hasAtelier: boolean = false;

  isUploading: boolean = false;
  isSubmitting: boolean = false;

  // ===== IMAGE UPLOAD MODE =====
  imageUploadMode: ImageUploadMode = 'direct';

  // Aperçu (mode "imageproduit")
  previewOriginalUrl: string | null = null;
  previewProcessedUrl: string | null = null;
  isAwaitingValidation: boolean = false;

  categoryInput: string = '';
  categoryInputArab: string = '';
  categoryInputAnglais: string = '';

  // ⭐ Langues de l'agence sélectionnée
  selectedAgencyLanguages: string[] = ['FR'];
  isLoadingLanguages: boolean = false;

  // 🔍 DÉBOGUEUR
  debugLangues: string = '';
  debugSelection: string = '';

  // ✅ Sélection manuelle (fallback)
  manualLanguages: { [key: string]: boolean } = {
    'FR': true,
    'AR': false,
    'EN': false
  };

  languageLabels: { [key: string]: string } = {
    'FR': 'Français',
    'AR': 'العربية',
    'EN': 'English'
  };

  constructor(
    private router: Router,
    private agenciesService: AgenciesService,
    private servicesService: ServicesService,
    private awsService: AwscontrolService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadAgencies();
    this.service.categoryList = [];
    this.service.categoryListarab = [];
    this.service.categoryListanglais = [];
    this.service.nomarab = '';
    this.service.nomanglais = '';

    this.filteredAgencies = this.agencyControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAgencies(value))
    );

    this.agencyControl.valueChanges.subscribe(value => {
      console.log('📡 valueChanges émis :', value);
      this.debugSelection = JSON.stringify(value);
      if (value && typeof value === 'object' && value.id) {
        this.onAgencySelected(value);
      } else if (!value || (typeof value === 'string' && value.trim() === '')) {
        this.service.agencyName = '';
        this.selectedAgencyLanguages = ['FR'];
        this.debugLangues = JSON.stringify(this.selectedAgencyLanguages);
        this.cdr.detectChanges();
      }
    });
  }

  loadAgencies(): void {
    this.agenciesService.getList().subscribe(
      (data: any) => {
        this.agenciesList = data.filter((a: any) => a.role === 'agence-service');
        console.log('📋 Agences "agence-service" chargées :', this.agenciesList.length);
      },
      (error: any) => {
        console.error('❌ Erreur chargement agences :', error);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de charger la liste des agences' });
      }
    );
  }

  filterAgencies(value: any): any[] {
    if (!value) return this.agenciesList;
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return this.agenciesList.filter(agency =>
        agency.name.toLowerCase().includes(filterValue)
      );
    }
    return this.agenciesList;
  }

  displayAgencyName(agency: any): string {
    return agency && agency.name ? agency.name : '';
  }

  onAgencySelected(agency: any): void {
    console.log('🔹 [onAgencySelected] Agence reçue :', agency);

    if (!agency || !agency.id) {
      console.warn('⚠️ Agence invalide (pas d\'ID)');
      return;
    }

    this.isLoadingLanguages = true;
    this.cdr.detectChanges();

    this.agenciesService.getAgencieById(agency.id).subscribe({
      next: (fullAgency: any) => {
        const langs = fullAgency.availableLanguages;
        if (Array.isArray(langs) && langs.length > 0) {
          this.selectedAgencyLanguages = [...langs];
        } else {
          this.selectedAgencyLanguages = ['FR'];
        }

        this.syncManualLanguages(this.selectedAgencyLanguages);
        this.service.agencyName = fullAgency.name || agency.name;
        this.agencyControl.setValue(fullAgency, { emitEvent: false });
        this.isLoadingLanguages = false;
        this.debugLangues = JSON.stringify(this.selectedAgencyLanguages);
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('❌ Erreur getAgencieById :', err);
        this.selectedAgencyLanguages = ['FR'];
        this.syncManualLanguages(['FR']);
        this.service.agencyName = agency.name;
        this.agencyControl.setValue(agency, { emitEvent: false });
        this.isLoadingLanguages = false;
        this.debugLangues = JSON.stringify(this.selectedAgencyLanguages);
        this.cdr.detectChanges();
      }
    });
  }

  syncManualLanguages(langs: string[]): void {
    this.manualLanguages['FR'] = langs.includes('FR');
    this.manualLanguages['AR'] = langs.includes('AR');
    this.manualLanguages['EN'] = langs.includes('EN');
  }

  onManualLanguageChange(): void {
    const selected: string[] = [];
    if (this.manualLanguages['FR']) selected.push('FR');
    if (this.manualLanguages['AR']) selected.push('AR');
    if (this.manualLanguages['EN']) selected.push('EN');
    if (selected.length === 0) {
      this.manualLanguages['FR'] = true;
      selected.push('FR');
    }
    this.selectedAgencyLanguages = selected;
    this.debugLangues = JSON.stringify(selected);
    this.cdr.detectChanges();
  }

  testSelectAgency(): void {
    const testAgency = this.agenciesList.find(a => a.name === 'ARTYFUN ');
    if (testAgency) {
      this.onAgencySelected(testAgency);
    } else {
      Swal.fire({ icon: 'warning', title: 'ARTYFUN introuvable', text: 'Cette agence n\'existe pas dans la liste filtrée.' });
    }
  }

  formatText(command: string): void {
    if (!this.service.description) this.service.description = '';
    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.service.description.substring(start, end);
    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'list': {
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
      }
      case 'linebreak':
        formattedText = '<br>';
        break;
      default: return;
    }
    const newText = this.service.description.substring(0, start) + formattedText + this.service.description.substring(end);
    this.service.description = newText;
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  addCategory(): void {
    if (this.categoryInput.trim()) {
      if (!this.service.categoryList) this.service.categoryList = [];
      if (!this.service.categoryList.includes(this.categoryInput.trim())) {
        this.service.categoryList.push(this.categoryInput.trim());
      }
      this.categoryInput = '';
    }
  }

  removeCategory(index: number): void {
    if (this.service.categoryList) {
      this.service.categoryList.splice(index, 1);
    }
  }

  addCategoryArab(): void {
    if (this.categoryInputArab.trim()) {
      if (!this.service.categoryListarab) this.service.categoryListarab = [];
      if (!this.service.categoryListarab.includes(this.categoryInputArab.trim())) {
        this.service.categoryListarab.push(this.categoryInputArab.trim());
      }
      this.categoryInputArab = '';
    }
  }

  removeCategoryArab(index: number): void {
    if (this.service.categoryListarab) {
      this.service.categoryListarab.splice(index, 1);
    }
  }

  addCategoryAnglais(): void {
    if (this.categoryInputAnglais.trim()) {
      if (!this.service.categoryListanglais) this.service.categoryListanglais = [];
      if (!this.service.categoryListanglais.includes(this.categoryInputAnglais.trim())) {
        this.service.categoryListanglais.push(this.categoryInputAnglais.trim());
      }
      this.categoryInputAnglais = '';
    }
  }

  removeCategoryAnglais(index: number): void {
    if (this.service.categoryListanglais) {
      this.service.categoryListanglais.splice(index, 1);
    }
  }

  // ==================== IMAGE UPLOAD ====================

  /**
   * Change le mode : 'direct' ou 'imageproduit'
   * Appelé depuis un bouton simple.
   */
  setUploadMode(mode: ImageUploadMode): void {
    if (this.isUploading) return;
    if (this.isAwaitingValidation) {
      this.cancelImagePreview(true);
    }
    this.imageUploadMode = mode;
    console.log('🔄 Mode upload :', mode);
    this.cdr.detectChanges();
  }

  /**
   * Déclenche l'ouverture du sélecteur de fichier.
   * Appelé par un simple bouton (pas de label, pas de click complexe).
   */
  triggerFileInput(): void {
    const input = document.getElementById('mainImageInput') as HTMLInputElement;
    if (input) {
      input.value = '';
      input.click();
    }
  }

  /**
   * Sélection d'un fichier → selon le mode, upload direct ou prévisualise.
   */
  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!this.validateImageFile(file)) {
      target.value = '';
      return;
    }

    this.selectedFiles = files;

    // Aperçu local de l'original
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewOriginalUrl = (e.target?.result as string) || null;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);

    if (this.imageUploadMode === 'direct') {
      this.uploadMainImage();
    } else {
      this.uploadMainImageViaProduit();
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', confirmButtonColor: '#ff9800' });
      return false;
    }
    return true;
  }

  uploadMainImage(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    const file = this.selectedFiles[0];

    this.isUploading = true;
    const folder = 'service-images';

    this.awsService.uploadFile(file, folder).subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const url = response.urls[0];
          this.uploadedMainImage = url;
          this.service.imageUrl = url;
          this.previewOriginalUrl = null;
          Swal.fire({ icon: 'success', title: 'Succès', text: 'Image téléchargée', timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Échec du téléchargement', confirmButtonColor: '#f44336' });
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger l\'image', confirmButtonColor: '#f44336' });
        this.cdr.detectChanges();
      }
    });
  }

  uploadMainImageViaProduit(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    const file = this.selectedFiles[0];

    this.isUploading = true;
    this.isAwaitingValidation = false;
    this.previewProcessedUrl = null;

    const folder = 'service-images';

    this.awsService.imageproduit([file], folder).subscribe({
      next: (response: ImageUploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.previewProcessedUrl = response.urls[0];
          this.isAwaitingValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement de l\'image',
            confirmButtonColor: '#f44336'
          });
          this.previewOriginalUrl = null;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isUploading = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image', confirmButtonColor: '#f44336' });
        this.previewOriginalUrl = null;
        this.cdr.detectChanges();
      }
    });
  }

  validateImagePreview(): void {
    if (!this.previewProcessedUrl) return;

    this.uploadedMainImage = this.previewProcessedUrl;
    this.service.imageUrl = this.previewProcessedUrl;

    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    this.isAwaitingValidation = false;
    this.selectedFiles = null;

    const input = document.getElementById('mainImageInput') as HTMLInputElement;
    if (input) input.value = '';

    this.cdr.detectChanges();

    Swal.fire({
      icon: 'success',
      title: 'Image validée',
      text: 'L\'image a été acceptée pour ce service.',
      timer: 1500,
      showConfirmButton: false
    });
  }

  cancelImagePreview(silent: boolean = false): void {
    const urlToDelete = this.previewProcessedUrl;

    const doCancel = () => {
      if (urlToDelete) {
        this.awsService.deleteFile(urlToDelete).subscribe({
          next: () => console.log('🗑️ Image refusée supprimée du S3'),
          error: (err) => console.warn('⚠️ Impossible de supprimer du S3 :', err)
        });
      }
      this.previewOriginalUrl = null;
      this.previewProcessedUrl = null;
      this.isAwaitingValidation = false;
      this.selectedFiles = null;

      const input = document.getElementById('mainImageInput') as HTMLInputElement;
      if (input) input.value = '';

      this.cdr.detectChanges();
    };

    if (silent) {
      doCancel();
      return;
    }

    Swal.fire({
      title: 'Refuser cette image ?',
      text: 'L\'image traitée sera supprimée et vous pourrez en choisir une autre.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(result => {
      if (result.isConfirmed) doCancel();
    });
  }

  removeMainImage(): void {
    this.uploadedMainImage = null;
    this.service.imageUrl = '';
    this.previewOriginalUrl = null;
    this.previewProcessedUrl = null;
    const input = document.getElementById('mainImageInput') as HTMLInputElement;
    if (input) input.value = '';
  }

  // ==================== SUBMIT ====================

  onSubmit(): void {
    if (this.isAwaitingValidation) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation requise',
        text: 'Veuillez valider ou refuser l\'image avant de continuer.',
        confirmButtonColor: '#ff9800'
      });
      return;
    }

    if (!this.service.name || this.service.name.trim() === '') {
      Swal.fire({ icon: 'warning', title: 'Nom requis', text: 'Veuillez saisir un nom pour le service', confirmButtonColor: '#ff9800' });
      return;
    }

    const selectedAgency = this.agencyControl.value;
    if (!selectedAgency || typeof selectedAgency === 'string') {
      Swal.fire({ icon: 'warning', title: 'Agence requise', text: 'Veuillez sélectionner une agence dans la liste', confirmButtonColor: '#ff9800' });
      return;
    }

    const agencyName = selectedAgency.name;
    if (!agencyName) {
      Swal.fire({ icon: 'warning', title: 'Agence invalide', text: 'L\'agence sélectionnée n\'a pas de nom', confirmButtonColor: '#ff9800' });
      return;
    }

    this.isSubmitting = true;

    const serviceData = {
      name: this.service.name.trim(),
      nomarab: this.service.nomarab?.trim() || '',
      nomanglais: this.service.nomanglais?.trim() || '',
      description: this.service.description || '',
      agencyName: agencyName,
      categoryList: this.service.categoryList || [],
      categoryListarab: this.service.categoryListarab || [],
      categoryListanglais: this.service.categoryListanglais || [],
      imageUrl: this.service.imageUrl || '',
      videoUrl: this.videoUrl || '',
      hasAtelier: this.hasAtelier
    };

    console.log('Service data to submit:', serviceData);

    this.servicesService.createService(serviceData).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        const serviceId = response.id || response._id || response.serviceId || 'ID inconnu';
        const agencyId = selectedAgency.id || selectedAgency._id || selectedAgency.agencyId;

        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: `Service créé avec succès (ID: ${serviceId}) pour l'agence ${agencyName}`,
          confirmButtonColor: '#4caf50',
          timer: 4000,
          showConfirmButton: true
        }).then(() => {
          this.router.navigate(['/list-Services', agencyId]);
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la création du service';
    if (error.error?.message) errorMessage = error.error.message;
    else if (error.status === 400) errorMessage = 'Données invalides';
    else if (error.status === 500) errorMessage = 'Erreur interne du serveur';

    Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser?',
      text: 'Toutes les données seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui'
    }).then((result) => {
      if (result.isConfirmed) {
        this.service = new Service();
        this.service.categoryList = [];
        this.service.categoryListarab = [];
        this.service.categoryListanglais = [];
        this.service.nomarab = '';
        this.service.nomanglais = '';
        this.uploadedMainImage = null;
        this.videoUrl = '';
        this.categoryInput = '';
        this.categoryInputArab = '';
        this.categoryInputAnglais = '';
        this.hasAtelier = false;
        this.agencyControl.setValue('');
        this.service.agencyName = '';
        this.selectedAgencyLanguages = ['FR'];
        this.debugLangues = JSON.stringify(this.selectedAgencyLanguages);
        this.syncManualLanguages(['FR']);

        this.previewOriginalUrl = null;
        this.previewProcessedUrl = null;
        this.isAwaitingValidation = false;
        this.imageUploadMode = 'direct';

        const input = document.getElementById('mainImageInput') as HTMLInputElement;
        if (input) input.value = '';

        this.cdr.detectChanges();
      }
    });
  }

  cancel(): void {
    this.router.navigate(['listeServices']);
  }
}